// src/pages/Maxsulotqosh.js
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import "./Maxsulotqosh.css";
import api from "../api";
import { getWarehouseAccess } from "../auth";
import { useSklad } from "../SkladContext";

const PAGE_SIZE = 24;
const RENDER_BATCH = 24;
const MAX_RENDERED = 5240;

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/+$/, "") || "http://";

const API_ORIGIN = (() => {
  try {
    const u = new URL(BASE_URL);
    return u.origin;
  } catch {
    return "http://localhost:8000";
  }
})();

/* ============ Decimal helpers ============ */
const normDecStr = (v) => {
  if (v == null) return "";
  let s = String(v).trim().replace(",", ".");
  s = s.replace(/[^\d.-]/g, "");
  if (s.includes("-")) s = (s.startsWith("-") ? "-" : "") + s.replace(/-/g, "");
  const i = s.indexOf(".");
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
  if (["", "-", ".", "-."].includes(s)) return "";
  return s;
};
const trimTrailingZeros = (v) => {
  const s = normDecStr(v);
  if (!s) return "";
  if (!s.includes(".")) return s;
  return s.replace(/(\.\d*?[1-9])0+$/u, "$1").replace(/\.0+$/u, "").replace(/\.$/u, "");
};
const toNum = (v) => {
  const n = Number(normDecStr(v));
  return Number.isFinite(n) ? n : 0;
};
const fmtMoney = (v, { min = 0, max = 3 } = {}) =>
  toNum(v).toLocaleString("uz-UZ", { minimumFractionDigits: min, maximumFractionDigits: max }) + " $";
const fmtQty = (v, { min = 0, max = 3 } = {}) =>
  toNum(v).toLocaleString("uz-UZ", { minimumFractionDigits: min, maximumFractionDigits: max });

const asArr = (res) => (Array.isArray(res?.data) ? res.data : res?.data?.results || []);
const hasNext = (res) => Boolean(res?.data?.next);

/* ============ Series sort helpers (YT-0001) ============ */
const seriesKey = (s) => {
  const str = String(s || "").trim().toUpperCase();
  const m = str.match(/^([A-Z]+)[\s\-_/]*0*(\d+)\s*$/);
  if (m) return { prefix: m[1], num: parseInt(m[2], 10) || 0, raw: str };
  return { prefix: "", num: Number.MAX_SAFE_INTEGER, raw: str };
};
const cmpSeries = (a, b) => {
  const A = seriesKey(a?.seriyasi);
  const B = seriesKey(b?.seriyasi);
  if (A.prefix !== B.prefix) return A.prefix.localeCompare(B.prefix);
  if (A.num !== B.num) return A.num - B.num;
  return A.raw.localeCompare(B.raw);
};

/* ============ DEDUPE helpers ============ */
const dedupeById = (arr) => {
  const m = new Map();
  for (const x of arr || []) {
    const id = x?.id;
    const key = id != null ? String(id) : `${x?.sklad || ""}::${x?.seriyasi || ""}::${x?.nom || ""}`;
    if (!m.has(key)) m.set(key, x);
  }
  return Array.from(m.values());
};

const normName = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "")
    .replace(/[`'"]/g, "");

const sellerLabel = (s) =>
  s ? `${(s.ismi || "").trim() || "Nomsiz"}${s.Dokonnomi ? ` - ${s.Dokonnomi}` : ""}` : "";

const normalizeEditComparable = (source = {}) => ({
  category: String(source.category || ""),
  brand: String(source.brand || ""),
  warehouse: String(source.warehouse || ""),
  name: String(source.name || "").trim(),
  quantity: trimTrailingZeros(source.quantity ?? ""),
  costPrice: trimTrailingZeros(source.costPrice ?? ""),
  series: String(source.series || "").trim(),
  description: String(source.description || "").trim(),
});

const hasNonImageEditChanges = (current, baseline) => {
  if (!baseline) return false;
  const normalizedCurrent = normalizeEditComparable(current);
  return Object.keys(baseline).some((key) => normalizedCurrent[key] !== baseline[key]);
};

const extractApiErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (Array.isArray(data)) return data.join(", ");
  if (typeof data === "object") {
    for (const value of Object.values(data)) {
      const message = extractApiErrorMessage(value, "");
      if (message) return message;
    }
  }
  return fallback;
};

/* ============ ComboSelect (autocomplete input) ============ */
function ComboSelect({ value, onChange, items, placeholder, disabled = false, getLabel }) {
  const labelOf = getLabel || ((it) => it.nom || it.name || "");
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef(null);

  const selectedName = useMemo(() => {
    if (!value) return "";
    const it = items.find((x) => String(x.id) === String(value));
    return it ? labelOf(it) : "";
  }, [value, items, labelOf]);

  const suggestions = useMemo(() => {
    if (!focused) return [];
    const q = text.trim().toLowerCase();
    if (!q) return items.slice(0, 40);
    return items.filter((x) => labelOf(x).toLowerCase().includes(q));
  }, [items, text, focused, labelOf]);

  useEffect(() => {
    const fn = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setFocused(false);
        setText("");
      }
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const handleFocus = () => {
    setFocused(true);
    setText("");
    setOpen(true);
  };

  const handleChange = (e) => {
    setText(e.target.value);
    if (!e.target.value) onChange("");
    setOpen(true);
  };

  const handleSelect = (item) => {
    onChange(String(item.id));
    setText("");
    setFocused(false);
    setOpen(false);
  };

  return (
    <div className="combo-wrap" ref={wrapRef}>
      <input
        type="text"
        className="combo-input"
        placeholder={placeholder}
        value={focused ? text : selectedName}
        onFocus={handleFocus}
        onChange={handleChange}
        disabled={disabled}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="combo-dropdown">
          {value && (
            <button
              type="button"
              className="combo-option combo-clear"
              onMouseDown={() => { onChange(""); setText(""); setFocused(false); setOpen(false); }}
            >
              — Tozalash
            </button>
          )}
          {suggestions.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`combo-option${String(item.id) === String(value) ? " active" : ""}`}
              onMouseDown={() => handleSelect(item)}
            >
              {labelOf(item)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const SellerSelect = ({ value, onChange, sellers, placeholder = "Tanlang", disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  const selected = sellers.find((s) => String(s.id) === String(value));
  const filtered = useMemo(() => {
    const key = (q || "").trim().toLowerCase();
    if (!key) return sellers;
    return sellers.filter((s) => {
      const nm = (s.ismi || "").toLowerCase();
      const dn = (s.Dokonnomi || "").toLowerCase();
      const tel = `${s.telraqam1 || ""} ${s.telraqam2 || ""}`.toLowerCase();
      return nm.includes(key) || dn.includes(key) || tel.includes(key);
    });
  }, [sellers, q]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className={`seller-select ${open ? "open" : ""}`} ref={ref}>
      <button
        type="button"
        className="seller-btn"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span className={selected ? "has-value" : "placeholder"}>
          {selected ? sellerLabel(selected) : placeholder}
        </span>
        <span className="chev">▾</span>
      </button>
      {open && (
        <div className="seller-panel">
          <input
            type="text"
            className="seller-search"
            placeholder="Haridor qidirish..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="seller-list">
            {filtered.length === 0 && <div className="seller-empty">Topilmadi</div>}
            {filtered.map((s) => (
              <button
                type="button"
                key={s.id}
                className="seller-item"
                onClick={() => {
                  onChange(String(s.id));
                  setOpen(false);
                }}
              >
                {sellerLabel(s)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Maxsulotqosh() {
  const warehouseAccess = useMemo(() => getWarehouseAccess(), []);
  const { allAccess, allowedIds } = warehouseAccess;

  // reference ro'yxatlar
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [priceTypes, setPriceTypes] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [dollarRate, setDollarRate] = useState(0);

  // server paging
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // qidiruv
  const [searchText, setSearchText] = useState("");
  const [q, setQ] = useState("");
  const searchTimer = useRef(null);

  // filtrlar
  const { activeSklad } = useSklad();
  const [warehouse, setWarehouse] = useState(allAccess ? activeSklad : "all");
  useEffect(() => {
    if (allAccess) setWarehouse(activeSklad);
  }, [activeSklad, allAccess]);
  const [category, setCategory] = useState("all");
  const [brand, setBrand] = useState("all");
  const [stockFilter, setStockFilter] = useState("available"); // available | out | all
  const [sortSeries, setSortSeries] = useState("asc"); // asc | desc

  // view mode
  const [viewMode, setViewMode] = useState("grid"); // grid | list

  // edit mode
  const [editingId, setEditingId] = useState(null);

  // form
  const [form, setForm] = useState({
    category: "",
    brand: "",
    warehouse: "",
    name: "",
    quantity: "",
    sourceSeller: "",
    costPrice: "",
    series: "YT-",
    barcodes: "",
    description: "",
    editNote: "",
    image: null,
    imagePreview: null,
    prices: {}, // { [narx_turi_id]: string decimal }
  });

  // mobile drawers
  const [mobileFormOpen, setMobileFormOpen] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // add-stock modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addTarget, setAddTarget] = useState(null);
  const [addQty, setAddQty] = useState("");
  const [addSeller, setAddSeller] = useState("");
  const [addNote, setAddNote] = useState("");
  const [addSubmitting, setAddSubmitting] = useState(false);

  // rasmni almashtirish (PATCH) uchun
  const imageInputRef = useRef(null);
  const [editImageFor, setEditImageFor] = useState(null);
  const editImageForRef = useRef(null);
  const editBaselineRef = useRef(null);

  // list so‘rovlarini bekor qilish uchun
  const listAbortRef = useRef(null);

  // DOM render optimizatsiya
  const allRef = useRef([]);              // barcha kelgan mahsulotlar
  const [renderCount, setRenderCount] = useState(RENDER_BATCH);
  const [items, setItems] = useState([]); // DOM'ga chiqariladiganlar

  // preview blob URL cleanup
  const lastPreviewRef = useRef(null);
  const revokePreview = () => {
    const u = lastPreviewRef.current;
    if (u && String(u).startsWith("blob:")) {
      URL.revokeObjectURL(u);
      lastPreviewRef.current = null;
    }
  };
  useEffect(() => () => revokePreview(), []);

  /* =================== INIT: reference ro'yxatlar =================== */
  useEffect(() => {
    (async () => {
      try {
        const [c, b, w, t, s, k] = await Promise.all([
          api.get("maxsulot-kategoriyasi/"),
          api.get("brend/"),
          api.get("sklad/"),
          api.get("narx-turi/"),
          api.get("sotuvchi/"),
          api.get("kurs/").catch(() => ({ data: {} })),
        ]);
        const allWarehouses = Array.isArray(w.data) ? w.data : w.data.results || [];
        const visibleWarehouses = allAccess
          ? allWarehouses
          : allWarehouses.filter((item) => allowedIds.includes(Number(item.id)));
        setCategories(Array.isArray(c.data) ? c.data : c.data.results || []);
        setBrands(Array.isArray(b.data) ? b.data : b.data.results || []);
        setWarehouses(visibleWarehouses);
        setPriceTypes(Array.isArray(t.data) ? t.data : t.data.results || []);
        setSellers(Array.isArray(s.data) ? s.data : s.data.results || []);
        setDollarRate(Number(k?.data?.naqd) || 0);

        // Cashier uchun: allowed sklad auto-select
        if (!allAccess && allowedIds.length === 1) {
          const sklad = visibleWarehouses.find((x) => Number(x.id) === allowedIds[0]);
          const prefix = sklad?.seriya_prefix || "YT-";
          setWarehouse(String(allowedIds[0]));
          setForm((prev) => ({ ...prev, warehouse: String(allowedIds[0]), series: prefix }));
        } else if (!allAccess && allowedIds.length > 1) {
          setWarehouse("all");
        }
      } catch (e) {
        console.log("Init load error", e?.response?.status, e?.response?.data);
        window.alert("Ro'yxatlarni yuklashda xatolik yuz berdi.");
      }
    })();
  }, [allAccess, allowedIds]);

  const resetList = useCallback(() => {
    setPage(1);
    setHasMore(true);
    allRef.current = [];
    setRenderCount(RENDER_BATCH);
    setItems([]);
  }, []);

  // filtr/sort o'zgarsa: 1-sahifadan qayta yuklash
  useEffect(() => {
    resetList();
  }, [warehouse, category, brand, stockFilter, sortSeries, resetList]);

  /* =================== PAGED LIST LOAD =================== */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try { listAbortRef.current?.abort(); } catch {}
      const controller = new AbortController();
      listAbortRef.current = controller;

      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("page_size", String(PAGE_SIZE));

        if ((q || "").trim()) params.set("search", q.trim());

        // filtrlar (backend qo'llasa ishlaydi; qo'llamasa fallback bor)
        if (warehouse !== "all") params.set("sklad", String(warehouse));
        if (category !== "all") params.set("kategoriya " , String(category)); // NOTE: agar backend "kategoriya" bo'lsa pastdagi satrni ishlating
        // ↑ agar sizda backend param nomi "kategoriya" bo'lsa, yuqoridagi "kategoriya " dagi bo'sh joyni o'chiring.
        if (category !== "all") params.set("kategoriya", String(category));
        if (brand !== "all") params.set("brand", String(brand));

        if (stockFilter === "available") params.set("available", "1");
        if (stockFilter === "out") params.set("out", "1");

        params.set("ordering", sortSeries === "asc" ? "seriyasi" : "-seriyasi");

        const url = `maxsulot/?${params.toString()}`;
        const res = await api.get(url, { signal: controller.signal });

        const results = asArr(res);
        const next = hasNext(res);

        if (cancelled) return;

        const mergedRaw = page === 1 ? results : [...allRef.current, ...results];

        // 1) DEDUPE (dublikat muammosini kesadi)
        let rows = dedupeById(mergedRaw);

        // 2) frontend fallback filter
        if (stockFilter === "available") rows = rows.filter((p) => toNum(p?.soni) > 0);
        if (stockFilter === "out") rows = rows.filter((p) => toNum(p?.soni) <= 0);

        // 3) frontend fallback sort (seriya)
        rows = [...rows].sort((a, b) =>
          sortSeries === "asc" ? cmpSeries(a, b) : cmpSeries(b, a)
        );

        allRef.current = rows;

        const safeCount = Math.min(renderCount, rows.length, MAX_RENDERED);
        setItems(rows.slice(0, safeCount));
        setHasMore(next);
      } catch (e) {
        if (e.name === "CanceledError" || e.name === "AbortError") return;
        const st = e?.response?.status;
        console.log("LIST LOAD ERR:", st, e?.response?.data);
        if (!cancelled && st === 404) setHasMore(false);
        else if (!cancelled) window.alert("Mahsulotlarni yuklashda xato.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [page, q, renderCount, warehouse, category, brand, stockFilter, sortSeries]);

  /* =================== SEARCH (debounce) =================== */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setQ(val.trim());
      resetList();
    }, 300);
  };
  useEffect(() => () => clearTimeout(searchTimer.current), [resetList]);

  /* =================== FORM HANDLERS =================== */
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "quantity" || name === "costPrice") {
      setF(name, normDecStr(value));
      return;
    }
    setF(name, value);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;
    revokePreview();
    const url = file ? URL.createObjectURL(file) : null;
    if (url) lastPreviewRef.current = url;

    setForm((prev) => ({
      ...prev,
      image: file,
      imagePreview: url,
    }));
  };

  const resetForm = useCallback(() => {
    setEditingId(null);
    editBaselineRef.current = null;
    revokePreview();
    setForm((prev) => {
      const wid = prev.warehouse;
      const sklad = warehouses.find((x) => String(x.id) === String(wid));
      const prefix = sklad?.seriya_prefix || "YT-";
      return {
        category: "",
        brand: "",
        warehouse: wid,
        name: "",
        quantity: "",
        sourceSeller: "",
        costPrice: "",
        series: prefix,
        barcodes: "",
        description: "",
        editNote: "",
        image: null,
        imagePreview: null,
        prices: {},
      };
    });
  }, [warehouses]);

  /* =================== EDIT =================== */
  const editProduct = useCallback(async (p) => {
    try {
      const { data } = await api.get(`maxsulot/${p.id}/`);
      const pricesMap = {};
      (data.narxlar || []).forEach((n) => {
        const t = priceTypes.find((x) => String(x.nom) === String(n.sarxturi));
        if (t) pricesMap[t.id] = trimTrailingZeros(n.narx ?? "");
      });

      const nextForm = {
        category: data.kategoriya ? String(data.kategoriya) : "",
        brand: data.brand ? String(data.brand) : "",
        warehouse: data.sklad ? String(data.sklad) : "",
        name: data.nom || "",
        quantity: trimTrailingZeros(String(data.soni ?? "")),
        sourceSeller: "",
        costPrice: trimTrailingZeros(String(data.tan_narxi ?? "")),
        series: data.seriyasi || "YT-",
        barcodes: data.barcodes || "",
        description: data.izoh || "",
        editNote: "",
        image: null,
        imagePreview:
          data.rasm_url ||
          (data.rasm ? (data.rasm.startsWith("http") ? data.rasm : `${API_ORIGIN}${data.rasm}`) : null),
        prices: pricesMap,
      };

      editBaselineRef.current = normalizeEditComparable(nextForm);
      setEditingId(p.id);
      setForm(nextForm);

      window.scrollTo({ top: 0, behavior: "smooth" });
      setMobileFormOpen(true);
    } catch (e) {
      console.log("EDIT LOAD ERR:", e?.response?.status, e?.response?.data);
      window.alert("Tahrirlash uchun ma'lumotlarni olishda xato.");
    }
  }, [priceTypes]);

  /* =================== CREATE/UPDATE submit =================== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) return window.alert("Nomi majburiy.");
    const needsEditNote = editingId && hasNonImageEditChanges(form, editBaselineRef.current);
    if (needsEditNote && !form.editNote?.trim()) {
      return window.alert("Tahrirlash izohi majburiy.");
    }
    try {
      const fd = new FormData();
      if (form.category)  fd.append("kategoriya", Number(form.category));
      if (form.brand)     fd.append("brand", Number(form.brand));
      if (form.warehouse) fd.append("sklad", Number(form.warehouse));
      fd.append("seriyasi", form.series || "");
      fd.append("nom", form.name || "");
      fd.append("soni", form.quantity ? form.quantity : "0");
      fd.append("tan_narxi", form.costPrice ? form.costPrice : "0");
      fd.append("barcodes", (form.barcodes || "").trim().replace(/\s+/g, " "));
      if (!editingId && form.sourceSeller) {
        fd.append("source_seller", Number(form.sourceSeller));
        const sel = sellers.find((x) => String(x.id) === String(form.sourceSeller));
        const label = sel ? `${sel.ismi || ""}${sel.Dokonnomi ? " - " + sel.Dokonnomi : ""}`.trim() : "";
        if (label) fd.append("yetkazib_beruvchi", label);
      }
      fd.append("izoh", form.description || "");
      if (editingId) fd.append("edit_note", form.editNote.trim());
      if (form.image) fd.append("rasm", form.image);

      let product = null;

      if (editingId) {
        const { data } = await api.patch(`maxsulot/${editingId}/`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        product = data;
      } else {
        const { data } = await api.post("maxsulot/", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        product = data;
      }

      const filled = Object.entries(form.prices)
        .map(([typeId, val]) => [typeId, normDecStr(val)])
        .filter(([, v]) => v !== "" && toNum(v) > 0);

      const existing = (editingId ? (product?.narxlar || []) : []);

      await Promise.all(
        filled.map(async ([typeId, val]) => {
          const tur = priceTypes.find((t) => String(t.id) === String(typeId));
          const sarxturi = tur?.nom || "";
          if (!sarxturi) return;

          const old = existing.find((x) => String(x.sarxturi) === String(sarxturi));
          if (old?.id) return api.patch(`maxsulot-narxi/${old.id}/`, { narx: val });

          return api.post("maxsulot-narxi/", {
            maxsulot: product.id,
            sarxturi,
            narx: val,
          });
        })
      );

      resetForm();
      resetList();
      setMobileFormOpen(false);

      window.alert(editingId ? "Mahsulot yangilandi ✅" : "Mahsulot saqlandi ✅");
    } catch (e2) {
      console.log("SAVE ERR:", e2?.response?.status, e2?.response?.data);
      if (e2?.response?.data?.seriyasi) {
        window.alert("Bu seriya allaqachon bor. Yangi seriya kiriting.");
        return;
      }
      window.alert(`Saqlashda xato: ${JSON.stringify(e2?.response?.data || e2.message)}`);
    }
  };

  /* =================== Narx chip edit/delete =================== */
  const handleEditPrice = async (productId, n) => {
    const raw = window.prompt(`Yangi narx (${n.sarxturi}, $)`, String(n.narx ?? ""));
    if (raw === null) return;
    const val = normDecStr(raw);
    if (val === "") return;
    try {
      const { data } = await api.patch(`maxsulot-narxi/${n.id}/`, { narx: val });
      allRef.current = allRef.current.map((p) =>
        p.id !== productId
          ? p
          : { ...p, narxlar: (p.narxlar || []).map((x) => (x.id === n.id ? data : x)) }
      );
      setItems(allRef.current.slice(0, Math.min(renderCount, allRef.current.length, MAX_RENDERED)));
    } catch (e) {
      window.alert("Narxni tahrirlashda xato.");
      console.log(e?.response?.status, e?.response?.data);
    }
  };

  const handleDeletePrice = async (productId, priceId) => {
    if (!window.confirm("Bu narx yozuvini o‘chiramizmi?")) return;
    try {
      await api.delete(`maxsulot-narxi/${priceId}/`);
      allRef.current = allRef.current.map((p) =>
        p.id !== productId
          ? p
          : { ...p, narxlar: (p.narxlar || []).filter((x) => x.id !== priceId) }
      );
      setItems(allRef.current.slice(0, Math.min(renderCount, allRef.current.length, MAX_RENDERED)));
    } catch (e) {
      window.alert("Narxni o‘chirishda xato.");
      console.log(e?.response?.status, e?.response?.data);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ushbu mahsulot o‘chirilsinmi?")) return;
    try {
      const { data } = await api.delete(`maxsulot/${id}/`);
      allRef.current = allRef.current.filter((p) => p.id !== id);
      setItems(allRef.current.slice(0, Math.min(renderCount, allRef.current.length, MAX_RENDERED)));
      if (data?.status === "archived" && data?.detail) {
        window.alert(data.detail);
      }
    } catch (e) {
      console.log("DELETE ERR:", e?.response?.status, e?.response?.data);
      if (e?.response?.data?.detail) {
        window.alert(e.response.data.detail);
        return;
      }
      window.alert("O‘chirishda xato.");
    }
  };

  /* =================== PATCH helper (rasmni almashtirish) =================== */
  const patchProduct = async (id, payload, file) => {
    if (file) {
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => fd.append(k, v));
      fd.append("rasm", file);
      return api.patch(`maxsulot/${id}/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    return api.patch(`maxsulot/${id}/`, payload);
  };

  const openImagePicker = (productId) => {
    editImageForRef.current = productId;
    setEditImageFor(productId);
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
      imageInputRef.current.click();
    }
  };

  const onPickNewImage = async (e) => {
    const file = e.target.files?.[0];
    const productId = editImageForRef.current || editImageFor;
    if (!file || !productId) {
      e.target.value = "";
      return;
    }
    try {
      const { data } = await patchProduct(productId, {}, file);
      allRef.current = allRef.current.map((p) => (p.id === productId ? { ...p, ...data } : p));
      setItems(allRef.current.slice(0, Math.min(renderCount, allRef.current.length, MAX_RENDERED)));
      if (data?.status === "archived" && data?.detail) {
        window.alert(data.detail);
      }
    } catch (err) {
      const message = extractApiErrorMessage(err?.response?.data, "Rasmni yangilashda xato.");
      window.alert(message);
      console.log(err?.response?.status, err?.response?.data);
    } finally {
      editImageForRef.current = null;
      setEditImageFor(null);
      e.target.value = "";
    }
  };

  const openAddModal = (product) => {
    setAddTarget(product);
    setAddQty("");
    setAddSeller("");
    setAddNote("");
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    if (addSubmitting) return;
    setAddModalOpen(false);
    setAddTarget(null);
  };

  const submitAddStock = async () => {
    if (!addTarget) return;
    const rawQty = normDecStr(addQty);
    if (!rawQty) return window.alert("Soni majburiy.");
    const qtyNum = Number(rawQty);  // o'nlik ruxsat (metr/kg)
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) return window.alert("Soni 0 dan katta bo‘lsin.");
    if (!addSeller) return window.alert("Kimdan kelgani majburiy.");

    try {
      setAddSubmitting(true);
      const payload = {
        qty: qtyNum,
        source_seller: Number(addSeller),
      };
      const note = (addNote || "").trim();
      if (note) payload.note = note;

      const { data } = await api.post(`maxsulot/${addTarget.id}/add-stock/`, payload);

      allRef.current = allRef.current.map((p) => (p.id === data.id ? { ...p, ...data } : p));
      setItems(allRef.current.slice(0, Math.min(renderCount, allRef.current.length, MAX_RENDERED)));

      closeAddModal();
    } catch (e) {
      console.log("ADD STOCK ERR:", e?.response?.status, e?.response?.data);
      window.alert("Mahsulot qo‘shishda xato.");
    } finally {
      setAddSubmitting(false);
    }
  };

  /* =================== Infinite scroll sentinel =================== */
  const loaderRef = useRef(null);
  useEffect(() => {
    if (!loaderRef.current) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        if (loading) return;

        const total = allRef.current.length;
        const canGrow = renderCount < Math.min(total, MAX_RENDERED);

        if (canGrow) {
          setRenderCount((c) => Math.min(c + RENDER_BATCH, total, MAX_RENDERED));
          return;
        }

        if (hasMore) setPage((p) => p + 1);
      },
      { rootMargin: "300px" }
    );

    io.observe(loaderRef.current);
    return () => io.disconnect();
  }, [loading, hasMore, renderCount]);

  const imgSrc = useCallback((p) => {
    if (p.imagePreview) return p.imagePreview;
    if (p.rasm_url) return p.rasm_url;
    if (p.rasm) return p.rasm.startsWith("http") ? p.rasm : `${API_ORIGIN}${p.rasm}`;
    return "https://via.placeholder.com/120x150?text=No+Image";
  }, []);

  /* ===================== PDF yorliq (QR: faqat seriyasi) ===================== */
  // currency: "usd" -> dollar narx, "som" -> so'm narx (kursga ko'paytirilgan)
  const downloadChekPdf = useCallback(async (p, currency = "usd") => {
    try {
      const seriya = String(p?.seriyasi || "").trim();
      if (!seriya) {
        window.alert("Seriyasi yo'q - QR uchun seriya majburiy.");
        return;
      }

      const qrDataUrl = await QRCode.toDataURL(seriya, {
        errorCorrectionLevel: "M",
        margin: 1,
        width: 500,
      });

      const LABEL_W = 30;
      const LABEL_H = 20;
      const PAD = 1;
      const QR_SIZE = 12.8;

      const pickLabelPrice = (prod) => {
        const prices = Array.isArray(prod?.narxlar) ? prod.narxlar : [];
        if (prices.length) {
          const byName = (needle) =>
            prices.find((x) => normName(x?.sarxturi).includes(needle));
          const dona = byName("dona");
          const optom = prices.find((x) => {
            const n = normName(x?.sarxturi);
            return n.includes("optom") || n.includes("ulgurji");
          });
          const picked = dona || optom || prices[0];
          return picked?.narx ?? null;
        }
        if (prod?.tan_narxi != null) return prod.tan_narxi;
        return null;
      };

      const fmtLabelPrice = (v) => {
        if (v == null || v === "") return "";
        const raw = String(v).trim();
        if (!raw) return "";
        const hasComma = raw.includes(",");
        const decSep = hasComma ? "," : ".";
        const normalized = raw.replace(",", ".").replace(/[^\d.-]/g, "");
        if (!normalized || ["-", ".", "-."].includes(normalized)) return "";
        const negative = normalized.startsWith("-");
        const safe = negative ? normalized.slice(1) : normalized;
        const [intRaw = "0", fracRaw = ""] = safe.split(".");
        const fracPart = fracRaw.replace(/0+$/, "");
        const intNum = Number(intRaw || "0");
        const intPart = Number.isFinite(intNum)
          ? intNum.toLocaleString("uz-UZ")
          : (intRaw || "0");
        const sign = negative ? "-" : "";
        return fracPart ? `${sign}${intPart}${decSep}${fracPart} $` : `${sign}${intPart} $`;
      };

      const nameRaw = String(p?.nom || "").trim();
      const name = nameRaw.length > 28 ? `${nameRaw.slice(0, 28)}...` : nameRaw;
      const rawPrice = pickLabelPrice(p);
      const usdNum = Number(String(rawPrice ?? "").replace(",", ".").replace(/[^\d.-]/g, "")) || 0;
      const somNum = usdNum * (Number(dollarRate) || 0);
      const priceStr =
        currency === "som"
          ? (somNum > 0 ? `${Math.round(somNum).toLocaleString("uz-UZ")} so'm` : "")
          : fmtLabelPrice(rawPrice);

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: [LABEL_W, LABEL_H],
      });

      doc.addImage(qrDataUrl, "PNG", PAD, PAD, QR_SIZE, QR_SIZE);

      const rightX = PAD + QR_SIZE + 1;
      const rightW = LABEL_W - rightX - PAD;
      const centerX = rightX + rightW / 2;

      const drawFittedCenteredText = (text, center, y, maxWidth, {
        font = "helvetica",
        style = "normal",
        size = 10,
        minSize = 6,
      } = {}) => {
        if (!text) return;
        doc.setFont(font, style);
        let fontSize = size;
        doc.setFontSize(fontSize);
        while (fontSize > minSize && doc.getTextWidth(text) > maxWidth) {
          fontSize -= 0.3;
          doc.setFontSize(fontSize);
        }
        doc.text(text, center, y, { align: "center" });
      };

      drawFittedCenteredText(seriya, centerX, 7, rightW, {
        style: "bold",
        size: 16.5,
        minSize: 10.5,
      });
      drawFittedCenteredText(priceStr, centerX, 13, rightW, {
        style: "bold",
        size: 10.5,
        minSize: 7.5,
      });
      drawFittedCenteredText(name, LABEL_W / 2, LABEL_H - 2.4, LABEL_W - PAD * 2, {
        size: 7.8,
        minSize: 6.4,
      });

      const safeFile = seriya.replace(/[^\w-]+/g, "_");
      doc.save(`chek_${safeFile}_${currency === "som" ? "som" : "usd"}.pdf`);
    } catch (e) {
      console.log("PDF/QR ERR:", e);
      window.alert("Chek PDF yaratishda xatolik.");
    }
  }, [dollarRate]);

  const titleText = editingId ? "Maxsulot tahrirlash" : "Maxsulot qo‘shish";

  const priceInputs = useMemo(() => (
    priceTypes.map((t) => (
      <input
        key={t.id}
        type="text"
        inputMode="decimal"
        placeholder={`${t.nom} ($)`}
        value={form.prices[t.id] || ""}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            prices: { ...prev.prices, [t.id]: normDecStr(e.target.value) },
          }))
        }
        onBlur={(e) =>
          setForm((prev) => ({
            ...prev,
            prices: { ...prev.prices, [t.id]: trimTrailingZeros(e.target.value) },
          }))
        }
      />
    ))
  ), [priceTypes, form.prices]);

  // Details viewda 2 ta ustun: Donaga, Optom (topilmasa first 2)
  const listPriceTypes = useMemo(() => {
    if (!priceTypes?.length) return [];
    const dona = priceTypes.find((t) => normName(t.nom).includes("dona"));
    const optom = priceTypes.find((t) => normName(t.nom).includes("optom") || normName(t.nom).includes("ulgurji"));
    const picked = [];
    if (dona) picked.push(dona);
    if (optom && optom.id !== dona?.id) picked.push(optom);
    // yetmasa: qolganlardan to‘ldiramiz
    for (const t of priceTypes) {
      if (picked.length >= 2) break;
      if (!picked.some((x) => x.id === t.id)) picked.push(t);
    }
    return picked.slice(0, 2);
  }, [priceTypes]);

  

  const getPriceByTypeName = useCallback((p, typeName) => {
    const n = (p?.narxlar || []).find((x) => String(x.sarxturi) === String(typeName));
    return n ? n.narx : "";
  }, []);

  const toggleView = () => setViewMode((m) => (m === "grid" ? "list" : "grid"));

  return (
    <div className="maxsulotqosh-page">

      {/* ===== MOBILE ACTION BAR ===== */}
      <div className="mob-bar">
        <button
          className="mob-bar-btn blue"
          onClick={() => { setMobileFormOpen((o) => !o); setMobileFilterOpen(false); }}
        >
          + {editingId ? "Tahrirlash" : "Qo'shish"}
        </button>
        <button
          className="mob-bar-btn"
          onClick={() => { setMobileFilterOpen((o) => !o); setMobileFormOpen(false); }}
        >
          ⚙ Filtr
        </button>
      </div>

      {mobileFormOpen && <div className="mob-bd" onClick={() => setMobileFormOpen(false)} />}
      {mobileFilterOpen && <div className="mob-bd" onClick={() => setMobileFilterOpen(false)} />}

      {/* ===== FORM SECTION ===== */}
      <div className={`mq-form-section fr-panel${mobileFormOpen ? " mob-open" : ""}`}>
        <div className="mq-sh">
          <h2>{titleText}</h2>
          <button className="mob-cls" type="button" onClick={() => setMobileFormOpen(false)}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="product-form">
          <ComboSelect
            value={form.brand}
            onChange={(v) => setF("brand", v)}
            items={brands}
            placeholder="Brend"
          />

          <ComboSelect
            value={form.category}
            onChange={(v) => setF("category", v)}
            items={categories}
            placeholder="Kategoriya"
          />

          <select
            value={form.warehouse}
            disabled={!allAccess && allowedIds.length === 1}
            onChange={(e) => {
              const wid = e.target.value;
              const sklad = warehouses.find((x) => String(x.id) === String(wid));
              const prefix = sklad?.seriya_prefix || "YT-";
              setForm((prev) => ({
                ...prev,
                warehouse: wid,
                ...(!editingId ? { series: prefix } : {}),
              }));
            }}
          >
            <option value="">Sklad tanlang</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.nom || w.name}
              </option>
            ))}
          </select>

          <input type="text" name="name" placeholder="Nomi" value={form.name} onChange={handleChange} />

          <input
            type="text"
            inputMode="decimal"
            name="quantity"
            placeholder="Soni (masalan 12.5)"
            value={form.quantity}
            onChange={handleChange}
            onBlur={(e) => setF("quantity", normDecStr(e.target.value))}
            disabled={!!editingId}
          />

          {!editingId && (
            <ComboSelect
              value={form.sourceSeller}
              onChange={(v) => setF("sourceSeller", v)}
              items={sellers}
              placeholder="Kimdan kelgani (ixtiyoriy)"
              getLabel={(s) =>
                s
                  ? `${(s.ismi || "").trim() || "Nomsiz"}${s.Dokonnomi ? ` - ${s.Dokonnomi}` : ""}`
                  : ""
              }
            />
          )}

          <input
            type="text"
            inputMode="decimal"
            name="costPrice"
            placeholder="Tan narxi ($, masalan 18.50)"
            value={form.costPrice}
            onChange={handleChange}
            onBlur={(e) => setF("costPrice", trimTrailingZeros(e.target.value))}
          />

          <input type="text" name="series" placeholder="Seriyasi" value={form.series} onChange={handleChange} />

          <input
            type="text"
            name="barcodes"
            placeholder="Shtrix/QR kodlar (bir nechta bo'lsa probel bilan)"
            value={form.barcodes}
            onChange={handleChange}
            title="Skaner bilan yoki qo'lda kiriting. 10-12 tagacha kod probel bilan saqlanadi."
          />

          <input type="text" name="description" placeholder="Izoh (ixtiyoriy)" value={form.description} onChange={handleChange} />

          {editingId && (
            <input
              type="text"
              name="editNote"
              placeholder="Tahrirlash izohi (ma'lumot o'zgarsa majburiy)"
              value={form.editNote}
              onChange={handleChange}
            />
          )}

          <div className="image-upload">
            <label className="upload-btn">
              Fayldan yuklash
              <input type="file" accept="image/*" onChange={handleImageChange} hidden />
            </label>
            {form.imagePreview && (
              <img src={form.imagePreview} alt="Preview" className="preview-img" />
            )}
          </div>

          <h3>Narxlar turlari</h3>
          {priceInputs}

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button type="submit" className="add-btn">
              {editingId ? "Saqlash" : "Qo‘shish"}
            </button>
            {editingId && (
              <button type="button" className="add-btn" onClick={resetForm}>
                Bekor qilish
              </button>
            )}
          </div>
        </form>
      </div>

      {/* ===== FILTER SECTION ===== */}
      <div className={`mq-filter-section fr-panel${mobileFilterOpen ? " mob-open" : ""}`}>
        <div className="mq-sh">
          <h3>Filtr & Qidiruv</h3>
          <button className="mob-cls" type="button" onClick={() => setMobileFilterOpen(false)}>×</button>
        </div>
        <div className="toolbar">
          <input
            className="search-input"
            placeholder="Nomi yoki seriya..."
            value={searchText}
            onChange={handleSearchChange}
          />

          <select value={warehouse} onChange={(e) => setWarehouse(e.target.value)}>
            <option value="all">Sklad: Hammasi</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>{w.nom || w.name}</option>
            ))}
          </select>

          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="all">Kategoriya: Barchasi</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.nom || c.name}</option>
            ))}
          </select>

          <select value={brand} onChange={(e) => setBrand(e.target.value)}>
            <option value="all">Brand: Barchasi</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>{b.nom || b.name}</option>
            ))}
          </select>

          <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)}>
            <option value="available">Qolgan</option>
            <option value="out">Qolmagan</option>
            <option value="all">Barchasi</option>
          </select>

          <select value={sortSeries} onChange={(e) => setSortSeries(e.target.value)}>
            <option value="asc">Seriya: Osish</option>
            <option value="desc">Seriya: Kamayish</option>
          </select>

          <button type="button" className="view-btn" onClick={toggleView} title="Ko’rinishni o’zgartirish">
            {viewMode === "grid" ? "Details" : "Grid"}
          </button>
        </div>
      </div>

      {/* ===== LIST SECTION ===== */}
      <div className="form-container fr-panel">
        <h3 className="list-title">
          Mahsulotlar ro‘yxati
          <span style={{ marginLeft: 10, fontSize: 12, opacity: 0.7 }}>
            (Ko‘rsatilmoqda: {items.length} / {allRef.current.length})
          </span>
        </h3>

        {/* ===== GRID VIEW ===== */}
        {viewMode === "grid" && (
          <div className="product-grid">
            {items.map((p) => (
              <div key={p.id} className="product-card fr-card">
                <div className="pc-left">
                  <img className="pc-img" src={imgSrc(p)} alt={p.nom || ""} loading="lazy" decoding="async" />
                  <button type="button" className="edit-btn block" onClick={() => editProduct(p)}>Tahrirlash</button>
                  <button type="button" className="add-stock-btn block" onClick={() => openAddModal(p)}>Qo‘shish</button>
                  <button type="button" className="edit-btn block" onClick={() => openImagePicker(p.id)}>Rasmni almashtirish</button>
                  <button type="button" className="edit-btn block" onClick={() => downloadChekPdf(p, "usd")}>QR chek ($)</button>
                  <button type="button" className="edit-btn block" onClick={() => downloadChekPdf(p, "som")}>QR chek (so'm)</button>
                </div>

                <div className="pc-body">
                  <div className="pc-row"><b>Nomi:</b> {p.nom}</div>
                  <div className="pc-row"><b>Soni:</b> {fmtQty(p.soni)} dona</div>
                  <div className="pc-row"><b>Seriyasi:</b> {p.seriyasi}</div>
                  <div className="pc-row"><b>Tan narxi:</b> {fmtMoney(p.tan_narxi)}</div>

                  {p.kategoriya_nom && <div className="pc-row"><b>Kategoriya:</b> {p.kategoriya_nom}</div>}
                  {p.brand_nom && <div className="pc-row"><b>Brand:</b> {p.brand_nom}</div>}
                  {p.sklad_nom && <div className="pc-row"><b>Sklad:</b> {p.sklad_nom}</div>}

                  <div className="pc-prices">
                    {(p.narxlar || []).map((n) => (
                      <div className="price-chip" key={n.id}>
                        <span className="label">{n.sarxturi}</span>
                        <span className="value">{fmtMoney(n.narx)}</span>
                        <button type="button" className="chip-btn" onClick={() => handleEditPrice(p.id, n)} title="Tahrirlash">✎</button>
                        <button type="button" className="chip-btn" onClick={() => handleDeletePrice(p.id, n.id)} title="O‘chirish">🗑</button>
                      </div>
                    ))}
                  </div>

                  <div className="pc-actions">
                    <button type="button" className="delete-btn" onClick={() => handleDelete(p.id)}>O‘chirish</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== LIST/DETAILS VIEW ===== */}
        {viewMode === "list" && (
          <div className="product-list">
            <div className="list-header">
              <div className="col img"></div>
              <div className="col series">seriyasi</div>
              <div className="col name">nomi</div>
              <div className="col qty">soni</div>
              <div className="col cost">tan narxi</div>

              {listPriceTypes.map((t) => (
                <div className="col price" key={t.id}>{String(t.nom || "").toLowerCase()}</div>
              ))}

              <div className="col cat">kategoriya</div>
              <div className="col brand">brand</div>
              <div className="col wh">sklad</div>
              <div className="col act">amallar</div>
            </div>

            {items.map((p) => (
              <div className="list-row" key={p.id}>
                <div className="col img">
                  <img className="li-img" src={imgSrc(p)} alt={p.nom || ""} loading="lazy" decoding="async" />
                </div>

                <div className="col series">{p.seriyasi}</div>

                <div className="col name">
                  <div className="li-strong">{p.nom}</div>
                </div>

                <div className="col qty">{fmtQty(p.soni)}</div>
                <div className="col cost">{fmtMoney(p.tan_narxi)}</div>

                {listPriceTypes.map((t) => (
                  <div className="col price" key={t.id}>
                    {getPriceByTypeName(p, t.nom) !== ""
                      ? fmtMoney(getPriceByTypeName(p, t.nom))
                      : ""}
                  </div>
                ))}

                <div className="col cat">{p.kategoriya_nom || ""}</div>
                <div className="col brand">{p.brand_nom || ""}</div>
                <div className="col wh">{p.sklad_nom || ""}</div>

                <div className="col act">
                  <div className="li-actions">
                    <button type="button" className="edit-btn" onClick={() => editProduct(p)}>Tahrirlash</button>
                    <button type="button" className="add-stock-btn" onClick={() => openAddModal(p)}>Qo‘shish</button>
                    <button type="button" className="edit-btn" onClick={() => openImagePicker(p.id)}>Rasm</button>
                    <button type="button" className="edit-btn" onClick={() => downloadChekPdf(p, "usd")}>QR ($)</button>
                    <button type="button" className="edit-btn" onClick={() => downloadChekPdf(p, "som")}>QR (so'm)</button>
                    <button type="button" className="delete-btn" onClick={() => handleDelete(p.id)}>O‘chirish</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {loading && <p className="loading">Yuklanmoqda...</p>}
        <div ref={loaderRef} style={{ height: 1 }} />

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={onPickNewImage}
          style={{ display: "none" }}
        />

        {addModalOpen && addTarget && (
          <div className="modal-backdrop" onClick={closeAddModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">Mahsulot qo‘shish</div>
                <button className="modal-close" onClick={closeAddModal} disabled={addSubmitting}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="modal-row">
                  <div className="modal-label">Mahsulot</div>
                  <div className="modal-value">
                    {addTarget.nom} {addTarget.seriyasi ? `(${addTarget.seriyasi})` : ""}
                  </div>
                </div>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="Soni (masalan 12.5)"
                  value={addQty}
                  onChange={(e) => setAddQty(normDecStr(e.target.value))}
                />
                <SellerSelect
                  value={addSeller}
                  onChange={(v) => setAddSeller(v)}
                  sellers={sellers}
                  placeholder="Kimdan kelgani (majburiy)"
                />
                <input
                  type="text"
                  placeholder="Izoh (ixtiyoriy)"
                  value={addNote}
                  onChange={(e) => setAddNote(e.target.value)}
                />
              </div>
              <div className="modal-foot">
                <button className="edit-btn" onClick={closeAddModal} disabled={addSubmitting}>
                  Bekor qilish
                </button>
                <button className="add-btn" onClick={submitAddStock} disabled={addSubmitting}>
                  {addSubmitting ? "Saqlanmoqda..." : "Qo‘shish"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
